import {
  createOntologyHeaders,
  getAuthenticationOrganizationId,
  getOntologyServiceBaseUrl
} from '@axilon-apps/domains-ontology-ui-state'
import type { PrepValues, SourceId, WizardState } from './types'
import {
  forgetIngestWizardRun,
  markIngestWizardRunStartedHere,
  resetIngestWizardBranchAdoption,
  type WizardBranchObservation
} from './wizardBranchAdoption'
import { buildWizardRequestHeaders } from './wizardRequestHeaders'

const API_BASE = `${getOntologyServiceBaseUrl()}/ingest-wizard-runs`
const SAVE_DEBOUNCE_MS = 150
const SECRET_FIELD_IDS = new Set([
  'apiKey',
  'apiToken',
  'avevaSqlPassword',
  'dbPassword',
  'mqttPassword',
  'opcUaPassword',
  'password',
  'webDevPassword',
  'gatewayLinkCertificate',
  'gatewayLinkPrivateKey',
  'gatewayLinkClientCa',
  'gatewayLinkSigningKey'
])

interface PersistedFile {
  clientId: string
  name: string
  size: number
  lastModified: number
  type: string
}

interface PersistedPrep extends Omit<
  PrepValues,
  'file' | 'files' | 'stagedFiles' | 'missingFiles'
> {
  files: PersistedFile[]
}

interface ServerStagedFile extends PersistedFile {
  key: string
}

interface PersistedWizard extends Omit<
  WizardState,
  'selected' | 'waived' | 'optionalSources' | 'launched' | 'prep'
> {
  selected: SourceId[]
  waived: string[]
  optionalSources: SourceId[]
  launched: SourceId[]
  prep: Partial<Record<SourceId, PersistedPrep>>
}

interface ServerWizardRun {
  id: string
  groupId: string
  status: 'draft' | 'pending_dispatch' | 'running' | 'paused' | 'completed' | 'abandoned'
  state: PersistedWizard
  execution?: {
    files?: Partial<Record<SourceId, ServerStagedFile[]>>
  }
  updatedAt: string
}

const filesByClientId = new Map<string, File>()
const idsByFile = new WeakMap<File, string>()
const runtimeDrafts = new Map<string, WizardState>()
const listeners = new Set<() => void>()
const serverRuns = new Map<string, Pick<ServerWizardRun, 'status' | 'updatedAt'>>()
const saveTimers = new Map<string, ReturnType<typeof setTimeout>>()
const stagePromises = new Map<string, Promise<void>>()
let loadedOrganizationId: string | null = null
let loadPromise: Promise<void> | null = null
let draftSnapshot: WizardState[] = []

function organizationKey(): string {
  return getAuthenticationOrganizationId() ?? 'anonymous'
}

function newClientId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `file-${Date.now()}-${Math.random()}`
}

function persistFile(file: File): PersistedFile {
  let clientId = idsByFile.get(file)
  if (!clientId) {
    clientId = newClientId()
    idsByFile.set(file, clientId)
    filesByClientId.set(clientId, file)
  }
  return {
    clientId,
    name: file.name,
    size: file.size,
    lastModified: file.lastModified,
    type: file.type
  }
}

function stripSecrets(fields: Record<string, string>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(fields).filter(([fieldId]) => !SECRET_FIELD_IDS.has(fieldId))
  )
}

function serializeWizard(state: WizardState): PersistedWizard {
  const prep: PersistedWizard['prep'] = {}
  for (const [sourceId, value] of Object.entries(state.prep) as Array<
    [SourceId, PrepValues | undefined]
  >) {
    if (!value) continue
    const files = value.files.length > 0 ? value.files : value.file ? [value.file] : []
    prep[sourceId] = {
      fields: stripSecrets(value.fields),
      checks: value.checks,
      ...(value.vendor ? { vendor: value.vendor } : {}),
      ...(value.tagMode ? { tagMode: value.tagMode } : {}),
      files: files.map(persistFile)
    }
  }
  return {
    ...state,
    selected: [...state.selected],
    waived: [...state.waived],
    optionalSources: [...state.optionalSources],
    launched: [...state.launched],
    prep
  }
}

function hydrateWizard(
  saved: PersistedWizard,
  execution: ServerWizardRun['execution'] = {}
): WizardState {
  const prep: WizardState['prep'] = {}
  for (const [sourceId, value] of Object.entries(saved.prep ?? {}) as Array<
    [SourceId, PersistedPrep | undefined]
  >) {
    if (!value) continue
    const stagedFiles = (execution?.files?.[sourceId] ?? []).map(
      ({ clientId, name, size, lastModified, type }) => ({
        clientId,
        name,
        size,
        lastModified,
        type
      })
    )
    const stagedIds = new Set(stagedFiles.map((file) => file.clientId))
    const resolved = value.files.map((file) => filesByClientId.get(file.clientId))
    const available = resolved.filter((file): file is File => file instanceof File)
    const missingFiles = value.files
      .filter((file, index) => !resolved[index] && !stagedIds.has(file.clientId))
      .map(({ name, size, lastModified, type }) => ({
        name,
        size,
        lastModified,
        type
      }))
    prep[sourceId] = {
      file: available[0] ?? null,
      files: available.length > 1 ? available : [],
      ...(stagedFiles.length > 0 ? { stagedFiles } : {}),
      fields: value.fields ?? {},
      checks: value.checks ?? {},
      ...(value.vendor ? { vendor: value.vendor } : {}),
      ...(value.tagMode ? { tagMode: value.tagMode } : {}),
      ...(missingFiles.length > 0 ? { missingFiles } : {})
    }
  }
  return {
    ...saved,
    runName: saved.runName ?? '',
    selected: new Set(saved.selected),
    waived: new Set(saved.waived),
    optionalSources: new Set(saved.optionalSources),
    launched: new Set(saved.launched),
    prep
  }
}

function refreshSnapshot(): void {
  draftSnapshot = [...runtimeDrafts.values()].sort((a, b) => b.updatedAt - a.updatedAt)
}

function emit(): void {
  refreshSnapshot()
  for (const listener of listeners) listener()
}

async function request<T>(path = '', init?: RequestInit): Promise<T> {
  const headers = buildWizardRequestHeaders(createOntologyHeaders(), init?.body, init?.headers)
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers
  })
  if (!response.ok) {
    const raw = await response.text().catch(() => '')
    let message = raw
    try {
      const parsed = JSON.parse(raw) as { message?: unknown }
      if (typeof parsed.message === 'string') message = parsed.message
    } catch {
      // Keep non-JSON server text.
    }
    throw new Error(message || `request failed (${response.status})`)
  }
  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

export async function reserveIngestWizardRunName(draftId: string, name: string): Promise<void> {
  await saveNow(draftId)
  const server = serverRuns.get(draftId)
  const updated = await request<ServerWizardRun>(`/${encodeURIComponent(draftId)}`, {
    method: 'PATCH',
    body: JSON.stringify({
      name,
      ...(server?.updatedAt ? { expectedUpdatedAt: server.updatedAt } : {})
    })
  })
  serverRuns.set(draftId, {
    status: updated.status,
    updatedAt: updated.updatedAt
  })
}

async function saveNow(draftId: string): Promise<void> {
  const timer = saveTimers.get(draftId)
  if (timer) clearTimeout(timer)
  saveTimers.delete(draftId)
  const state = runtimeDrafts.get(draftId)
  if (!state) return
  const server = serverRuns.get(draftId)
  const updated = await request<ServerWizardRun>(`/${encodeURIComponent(draftId)}`, {
    method: 'PATCH',
    body: JSON.stringify({
      state: serializeWizard(state),
      ...(server?.updatedAt ? { expectedUpdatedAt: server.updatedAt } : {})
    })
  })
  serverRuns.set(draftId, {
    status: updated.status,
    updatedAt: updated.updatedAt
  })
}

export function loadIngestWizardDrafts(): Promise<void> {
  const organizationId = organizationKey()
  if (loadedOrganizationId === organizationId && loadPromise) return loadPromise
  loadedOrganizationId = organizationId
  loadPromise = request<{ runs: ServerWizardRun[] }>()
    .then(({ runs }) => {
      runtimeDrafts.clear()
      serverRuns.clear()
      for (const run of runs) {
        if (run.status === 'completed' || run.status === 'abandoned') continue
        const state = hydrateWizard(
          {
            ...run.state,
            draftId: run.id,
            updatedAt: Date.parse(run.updatedAt),
            run: { ...run.state.run, groupId: run.groupId }
          },
          run.execution
        )
        runtimeDrafts.set(run.id, state)
        serverRuns.set(run.id, {
          status: run.status,
          updatedAt: run.updatedAt
        })
      }
      emit()
    })
    .catch(() => {
      // Keep the current optimistic snapshot during transient network failures.
      loadPromise = null
    })
  return loadPromise
}

function scheduleSave(draftId: string): void {
  const existing = saveTimers.get(draftId)
  if (existing) clearTimeout(existing)
  saveTimers.set(
    draftId,
    setTimeout(() => {
      saveTimers.delete(draftId)
      const state = runtimeDrafts.get(draftId)
      const server = serverRuns.get(draftId)
      if (!state || !server) return
      void request<ServerWizardRun>(`/${encodeURIComponent(draftId)}`, {
        method: 'PATCH',
        body: JSON.stringify({
          state: serializeWizard(state),
          expectedUpdatedAt: server.updatedAt
        })
      })
        .then((updated) => {
          serverRuns.set(draftId, {
            status: updated.status,
            updatedAt: updated.updatedAt
          })
        })
        .catch(() => {
          void loadIngestWizardDrafts()
        })
    }, SAVE_DEBOUNCE_MS)
  )
}

export function subscribeIngestWizardDrafts(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function getIngestWizardDraftsSnapshot(): WizardState[] {
  return draftSnapshot
}

export function getIngestWizardDraft(draftId: string): WizardState | undefined {
  return runtimeDrafts.get(draftId)
}

export async function refreshIngestWizardDraft(draftId: string): Promise<WizardBranchObservation> {
  const run = await request<ServerWizardRun>(`/${encodeURIComponent(draftId)}`)
  const observation = observeWizardBranch(run)
  if (run.status === 'completed' || run.status === 'abandoned') {
    runtimeDrafts.delete(draftId)
    serverRuns.delete(draftId)
    emit()
    return observation
  }
  const state = preserveRuntimeSecrets(
    hydrateWizard(
      {
        ...run.state,
        draftId: run.id,
        updatedAt: Date.parse(run.updatedAt),
        run: { ...run.state.run, groupId: run.groupId }
      },
      run.execution
    ),
    runtimeDrafts.get(draftId)
  )
  runtimeDrafts.set(run.id, state)
  serverRuns.set(run.id, { status: run.status, updatedAt: run.updatedAt })
  emit()
  return observation
}

function observeWizardBranch(run: ServerWizardRun): WizardBranchObservation {
  const outcome =
    run.status === 'completed'
      ? 'completed'
      : run.status === 'abandoned' || run.state.run?.pauseReason === 'error'
        ? 'failed'
        : 'active'
  return {
    ...(run.state.run?.branchId ? { branchId: run.state.run.branchId } : {}),
    outcome
  }
}

function preserveRuntimeSecrets(next: WizardState, current: WizardState | undefined): WizardState {
  if (!current) return next
  const prep = { ...next.prep }
  for (const sourceId of Object.keys(current.prep) as SourceId[]) {
    const currentPrep = current.prep[sourceId]
    const nextPrep = prep[sourceId]
    if (!currentPrep || !nextPrep) continue
    const secrets = Object.fromEntries(
      Object.entries(currentPrep.fields).filter(([fieldId]) => SECRET_FIELD_IDS.has(fieldId))
    )
    if (Object.keys(secrets).length > 0) {
      prep[sourceId] = {
        ...nextPrep,
        fields: { ...nextPrep.fields, ...secrets }
      }
    }
  }
  return { ...next, prep }
}

export async function startIngestWizardDraft(draftId: string): Promise<WizardBranchObservation> {
  markIngestWizardRunStartedHere(draftId)
  await stageInputs(draftId)
  await request(`/${encodeURIComponent(draftId)}/start`, { method: 'POST' })
  return refreshIngestWizardDraft(draftId)
}

export async function resumeIngestWizardDraft(draftId: string): Promise<WizardBranchObservation> {
  markIngestWizardRunStartedHere(draftId)
  await stageInputs(draftId)
  await request(`/${encodeURIComponent(draftId)}/resume`, { method: 'POST' })
  return refreshIngestWizardDraft(draftId)
}

export async function skipIngestWizardDraftSource(
  draftId: string
): Promise<WizardBranchObservation> {
  markIngestWizardRunStartedHere(draftId)
  await saveNow(draftId)
  await request(`/${encodeURIComponent(draftId)}/skip`, { method: 'POST' })
  return refreshIngestWizardDraft(draftId)
}

export async function abandonIngestWizardDraft(draftId: string): Promise<void> {
  await request(`/${encodeURIComponent(draftId)}/abandon`, { method: 'POST' })
  forgetIngestWizardRun(draftId)
  runtimeDrafts.delete(draftId)
  serverRuns.delete(draftId)
  emit()
}

async function stageInputs(draftId: string): Promise<void> {
  await saveNow(draftId)
  const state = runtimeDrafts.get(draftId)
  if (!state) throw new Error('Ingest wizard draft was not found')
  for (const [sourceId, prep] of Object.entries(state.prep) as Array<
    [SourceId, PrepValues | undefined]
  >) {
    if (!prep) continue
    await stageIngestWizardSourceFiles(draftId, sourceId)
    const secrets = Object.fromEntries(
      Object.entries(prep.fields).filter(([fieldId]) => SECRET_FIELD_IDS.has(fieldId))
    )
    if (Object.keys(secrets).length > 0) {
      await request(
        `/${encodeURIComponent(draftId)}/sources/${encodeURIComponent(sourceId)}/credentials`,
        {
          method: 'PUT',
          body: JSON.stringify({ fields: secrets })
        }
      )
    }
  }
}

export function stageIngestWizardSourceFiles(draftId: string, sourceId: SourceId): Promise<void> {
  const key = `${draftId}:${sourceId}`
  const previous = stagePromises.get(key) ?? Promise.resolve()
  const pending = previous
    .catch(() => undefined)
    .then(() => stageIngestWizardSourceFilesNow(draftId, sourceId))
  stagePromises.set(key, pending)
  return pending.finally(() => {
    if (stagePromises.get(key) === pending) stagePromises.delete(key)
  })
}

async function stageIngestWizardSourceFilesNow(draftId: string, sourceId: SourceId): Promise<void> {
  await saveNow(draftId)
  const prep = runtimeDrafts.get(draftId)?.prep[sourceId]
  if (!prep) return
  const files = prep.files.length > 0 ? prep.files : prep.file ? [prep.file] : []
  if (files.length === 0 && (prep.stagedFiles === undefined || prep.stagedFiles.length > 0)) {
    return
  }
  const persistedFiles = files.map(persistFile)
  if (
    persistedFiles.length > 0 &&
    persistedFiles.length === prep.stagedFiles?.length &&
    persistedFiles.every((file, index) => file.clientId === prep.stagedFiles?.[index]?.clientId)
  ) {
    return
  }
  const form = new FormData()
  for (const [index, file] of files.entries()) {
    const persisted = persistedFiles[index]!
    form.append(persisted.clientId, file, file.name)
    form.append(`lastModified.${persisted.clientId}`, String(file.lastModified))
  }
  const updated = await request<ServerWizardRun>(
    `/${encodeURIComponent(draftId)}/sources/${encodeURIComponent(sourceId)}/files`,
    {
      method: 'PUT',
      body: form
    }
  )
  const current = runtimeDrafts.get(draftId)
  if (current) {
    runtimeDrafts.set(
      draftId,
      preserveRuntimeSecrets(
        hydrateWizard(
          {
            ...updated.state,
            draftId: updated.id,
            updatedAt: Date.parse(updated.updatedAt),
            run: { ...updated.state.run, groupId: updated.groupId }
          },
          updated.execution
        ),
        current
      )
    )
    serverRuns.set(draftId, {
      status: updated.status,
      updatedAt: updated.updatedAt
    })
    emit()
  }
}

export async function createIngestWizardDraft(state: WizardState): Promise<WizardState> {
  await loadIngestWizardDrafts()
  const created = await request<ServerWizardRun>('', {
    method: 'POST',
    body: JSON.stringify({ state: serializeWizard(state) })
  })
  const next: WizardState = {
    ...state,
    draftId: created.id,
    updatedAt: Date.parse(created.updatedAt),
    run: { ...state.run, groupId: created.groupId }
  }
  runtimeDrafts.set(created.id, next)
  serverRuns.set(created.id, {
    status: created.status,
    updatedAt: created.updatedAt
  })
  emit()
  scheduleSave(created.id)
  return next
}

export function updateIngestWizardDraft(
  draftId: string,
  update: WizardState | ((state: WizardState) => WizardState)
): void {
  const current = runtimeDrafts.get(draftId)
  if (!current) return
  const next = typeof update === 'function' ? update(current) : update
  runtimeDrafts.set(draftId, { ...next, updatedAt: Date.now() })
  emit()
  scheduleSave(draftId)
}

export function removeIngestWizardDraft(draftId: string): void {
  const timer = saveTimers.get(draftId)
  if (timer) clearTimeout(timer)
  saveTimers.delete(draftId)
  if (!runtimeDrafts.delete(draftId)) return
  forgetIngestWizardRun(draftId)
  serverRuns.delete(draftId)
  emit()
  void request<void>(`/${encodeURIComponent(draftId)}/abandon`, {
    method: 'POST'
  })
}

export function clearIngestWizardDrafts(): void {
  for (const timer of saveTimers.values()) clearTimeout(timer)
  saveTimers.clear()
  stagePromises.clear()
  runtimeDrafts.clear()
  serverRuns.clear()
  resetIngestWizardBranchAdoption()
  emit()
}

export function resetIngestWizardFileRegistry(): void {
  filesByClientId.clear()
}

/** Test-only: force the next read to rehydrate from the server. */
export function resetIngestWizardRuntimeStore(): void {
  for (const timer of saveTimers.values()) clearTimeout(timer)
  saveTimers.clear()
  stagePromises.clear()
  runtimeDrafts.clear()
  serverRuns.clear()
  resetIngestWizardBranchAdoption()
  draftSnapshot = []
  loadedOrganizationId = null
  loadPromise = null
  listeners.clear()
}
